/**
 * ============================================================================
 * ClinIA Perú - Borrador de consulta en curso
 * ============================================================================
 * Guarda automáticamente lo que el médico va escribiendo, para que una pestaña
 * cerrada sin querer, un corte de luz o una sesión caducada no se lleven media
 * consulta escrita con el paciente delante.
 *
 * Se guarda en el navegador y no en el servidor a propósito:
 *   - Funciona aunque no haya conexión, que es justo cuando más falta hace.
 *   - Un borrador a medio escribir no es una consulta: no debe existir en la
 *     historia clínica hasta que el médico la guarde.
 *
 * El borrador se borra en cuanto la consulta se guarda de verdad.
 * ============================================================================
 */

const KEY_PREFIX = 'clinia:draft:consultation';

/** El borrador caduca en 12 horas: pasada la jornada ya no es útil y confunde */
const MAX_AGE_MS = 12 * 60 * 60 * 1000;

const storageKey = (doctorId) => `${KEY_PREFIX}:${doctorId || 'anon'}`;

/**
 * Guarda el borrador.
 *
 * Nunca lanza: si el almacenamiento está lleno o bloqueado (modo privado), se
 * pierde el autoguardado, pero la consulta que el médico está escribiendo no
 * debe romperse por eso.
 *
 * @param {string} doctorId
 * @param {Object} draft - Estado completo del formulario
 */
export const saveDraft = (doctorId, draft) => {
  try {
    localStorage.setItem(storageKey(doctorId), JSON.stringify({
      savedAt: Date.now(),
      draft,
    }));
  } catch {
    // Sin espacio o sin permiso: se sigue sin autoguardado.
  }
};

/**
 * Devuelve el borrador guardado, si existe y no ha caducado.
 *
 * @param {string} doctorId
 * @returns {{savedAt: number, draft: Object}|null}
 */
export const loadDraft = (doctorId) => {
  try {
    const raw = localStorage.getItem(storageKey(doctorId));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed?.draft || !parsed?.savedAt) return null;

    if (Date.now() - parsed.savedAt > MAX_AGE_MS) {
      clearDraft(doctorId);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

/** Elimina el borrador. Se llama al guardar la consulta o al descartarlo. */
export const clearDraft = (doctorId) => {
  try {
    localStorage.removeItem(storageKey(doctorId));
  } catch {
    // Nada que hacer.
  }
};

/**
 * Decide si un borrador merece recuperarse.
 *
 * Sin esto, abrir el diálogo y cerrarlo dejaría un borrador vacío que luego
 * pregunta "¿quieres recuperar?" sin nada que recuperar.
 *
 * @param {Object} draft
 * @returns {boolean}
 */
export const hasContent = (draft) => {
  if (!draft) return false;

  const { formData = {}, diagnoses = [], treatment = {} } = draft;

  const textos = [formData.reason, formData.clinical_evaluation, formData.tiempo_enfermedad,
                  formData.relato_enfermedad, formData.funciones_psicologicas];
  if (textos.some(t => (t || '').trim())) return true;

  if (diagnoses.some(d => (d?.diagnosis || '').trim())) return true;
  if ((treatment.medicamentos || []).some(m => (m?.nombre || '').trim())) return true;
  if ((treatment.nutricion || '').trim()) return true;

  return false;
};

/** Hace legible cuándo se guardó: "hace 3 minutos" */
export const describeAge = (savedAt) => {
  const minutes = Math.floor((Date.now() - savedAt) / 60000);
  if (minutes < 1) return 'hace unos segundos';
  if (minutes === 1) return 'hace 1 minuto';
  if (minutes < 60) return `hace ${minutes} minutos`;
  const hours = Math.floor(minutes / 60);
  return hours === 1 ? 'hace 1 hora' : `hace ${hours} horas`;
};
